// Optional props default via JS default parameters below. defaultProps is
// deprecated for function components in React 18+, so we opt out of the
// require-default-props rule here rather than reintroduce that warning.
/* eslint-disable react/require-default-props */
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Column,
  Dialog,
  DialogContent,
  IconButton,
  InputSelectField,
  Row,
  Typography,
} from '@tempmee/react';
import {
  OutlineXIcon,
  SolidCheckCircleIcon,
  SolidExclamationCircleIcon,
  SolidExclamationWarningAlertIcon,
} from '@tempmee/react-icons';
import { getLikelyToFillBand, useLikelyToFillExperiment } from '../../../domain/Jobs';
import styles from './SelectOfferRateModal.module.scss';

// The legacy picker's range for an office with no recommendation, and its
// baseline for the percentage (MobileAppModal).
const FALLBACK_MIN = 18;
const FALLBACK_MAX = 200;
const FALLBACK_AVERAGE = 36;

const TONE_CLASSES = {
  positive: { icon: 'text-success-500', label: 'text-success-500' },
  neutral: { icon: 'text-neutral-900', label: 'text-neutral-900' },
  warning: { icon: 'text-warning-500', label: 'text-warning-700' },
  negative: { icon: 'text-error-500', label: 'text-error-500' },
};

const BANNER_CLASSES = { warning: 'bg-warning', negative: 'bg-error' };

const ICONS = {
  positive: SolidCheckCircleIcon,
  neutral: SolidExclamationWarningAlertIcon,
  warning: SolidExclamationWarningAlertIcon,
  negative: SolidExclamationCircleIcon,
};

const percentageClass = (percentage) => {
  if (percentage < 85) return 'text-error-500';
  if (percentage < 100) return 'text-warning-500';
  return 'text-success-500';
};

const LikelyToFill = ({ indicator }) => {
  const Icon = ICONS[indicator.tone];
  const classes = TONE_CLASSES[indicator.tone];

  return (
    <Column gap={2} align="center" className={styles.feedback} role="status" aria-live="polite">
      <Row gap={1} align="center">
        <Icon size={20} className={classes.icon} aria-hidden="true" />
        <Typography variant="body1" bold className={classes.label}>
          {indicator.label}
        </Typography>
      </Row>
      {indicator.message && (
        <Box p={3} className={`${BANNER_CLASSES[indicator.tone]} rounded-lg`}>
          <Typography variant="body1" className="text-white">
            {indicator.message}
          </Typography>
        </Box>
      )}
    </Column>
  );
};

LikelyToFill.propTypes = {
  indicator: PropTypes.shape({
    tone: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    message: PropTypes.string,
  }).isRequired,
};

/**
 * "Select Offer Rate" for posting temp shifts: today's rate picker, plus the
 * Likely to Fill label (and alert) for offices in the GOTU-13065 treatment arm,
 * as they would see it. Control offices see today's picker unchanged. Mount it
 * to open it: the picker starts from `rate` (else the recommendation) each time.
 *
 * Interviews keep MobileAppModal's picker: the A/B test covers temp shifts only.
 */
export const SelectOfferRateModal = ({
  onClose,
  onConfirm,
  officeId = undefined,
  profession = undefined,
  subProfession = null,
  rate = '',
  recommendedRate = null,
  rateMin = null,
  rateMax = null,
}) => {
  const min = rateMin || FALLBACK_MIN;
  const max = rateMax || FALLBACK_MAX;
  const recommended = recommendedRate != null ? Math.round(recommendedRate) : null;
  const [value, setValue] = useState(() => Math.round(Number(rate)) || recommended || null);

  const options = useMemo(
    () =>
      Array.from({ length: max - min + 1 }, (_, index) => {
        const option = min + index;
        return {
          value: option,
          label: `${option}.00${option === recommended ? ' (Recommended)' : ''}`,
        };
      }),
    [min, max, recommended],
  );

  const { isTreatment, bands, isLoading } = useLikelyToFillExperiment(officeId);

  // Nothing until the office's arm is known, so the label never appears late or flickers.
  const indicator =
    !isLoading && isTreatment && value != null
      ? getLikelyToFillBand({ rate: value, recommendedRate, profession, subProfession, bands })
      : null;

  const percentage = value != null ? (value / (recommendedRate || FALLBACK_AVERAGE)) * 100 : 0;

  return (
    <Dialog open onClose={onClose} size="medium" className={styles.panel}>
      <DialogContent>
        <Column align="center" gap={4} py={4}>
          <Row justify="center" className={styles.header}>
            <Typography variant="h6" bold className="text-neutral-600">
              Select Offer Rate
            </Typography>
            <IconButton
              Icon={<OutlineXIcon size={20} className="text-neutral-300" />}
              aria-label="Close"
              appearance="default"
              color="secondary"
              size="small"
              className={styles.close}
              onClick={onClose}
            />
          </Row>

          <Row align="center" gap={2}>
            <Typography variant="body1">$</Typography>
            <Box className={styles.select}>
              <InputSelectField
                options={options}
                value={value ?? undefined}
                onChange={setValue}
                placeholder="Select a rate"
              />
            </Box>
          </Row>

          {indicator && <LikelyToFill indicator={indicator} />}

          <Column align="center" gap={1}>
            {recommended != null && (
              <Typography variant="body1">
                Recommended <strong>${recommended}</strong>/hr · Range ${min}–${max}
              </Typography>
            )}
            <Typography variant="body1" className={styles.percentage}>
              <Typography as="span" variant="body1" bold className={percentageClass(percentage)}>
                {percentage.toFixed(1)}%
              </Typography>{' '}
              of Industry average
            </Typography>
          </Column>

          <Box className={styles.confirm}>
            <Button
              variant="contained"
              color="secondary"
              className={styles.confirmButton}
              disabled={value == null}
              onClick={() => onConfirm(value)}
            >
              Confirm
            </Button>
          </Box>
        </Column>
      </DialogContent>
    </Dialog>
  );
};

SelectOfferRateModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  officeId: PropTypes.string,
  profession: PropTypes.string,
  subProfession: PropTypes.string,
  rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  recommendedRate: PropTypes.number,
  rateMin: PropTypes.number,
  rateMax: PropTypes.number,
};
